import { logger } from '@adornis/base/logging.js';
import type { Constructor } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadRawMongoData, mongoDataToEntity } from '@adornis/bh-util/util.js';
import { switchMap } from 'rxjs';
import { getProductByID } from './product.js';
import { ProductEntryRequirement } from '../entities/ProductEntryRequirement.js';

export async function checkWeatherUserMeetsEntryRequirements(userID: string, productID: string) {
  logger.info({ userID, productID }, 'checkWeatherUserMeetsEntryRequirements');
  const entryRequirementsData = await loadRawMongoData(ProductEntryRequirement, { productID });
  const entryRequirements = entryRequirementsData.map(e => {
    const node = baseqlMetaData.entities.find(node => (node.type() as typeof AdornisEntity)._class === e._class);
    const entity = node?.type() as Constructor<any>;
    return new entity(e);
  });

  for (const entryRequirement of entryRequirements) {
    const requirementIsMet = await entryRequirement.requirementIsMet(userID);
    if (!requirementIsMet) return { requirementsMet: false, reason: await entryRequirement.label() } as const;
  }

  return { requirementsMet: true } as const;
}

// Batched variant of `checkWeatherUserMeetsEntryRequirements`. One `$in` query per call,
// then per-product evaluation short-circuits on the first unmet requirement.
export async function loadUserEntryRequirementsStatus(
  userID: string,
  productIDs: string[],
): Promise<Map<string, { requirementsMet: true } | { requirementsMet: false; reason: any }>> {
  const result = new Map<string, { requirementsMet: true } | { requirementsMet: false; reason: any }>();
  const uniqueIDs = Array.from(new Set(productIDs.filter(Boolean)));
  if (uniqueIDs.length === 0) return result;

  logger.info({ userID, productIDs: uniqueIDs }, 'loadUserEntryRequirementsStatus');

  const entryRequirementsData = await loadRawMongoData(ProductEntryRequirement, { productID: { $in: uniqueIDs } });
  const byProduct = new Map<string, any[]>();
  for (const e of entryRequirementsData) {
    const list = byProduct.get(e.productID) ?? [];
    list.push(e);
    byProduct.set(e.productID, list);
  }

  for (const productID of uniqueIDs) {
    const dataForProduct = byProduct.get(productID) ?? [];
    const entryRequirements = dataForProduct.map(e => {
      const node = baseqlMetaData.entities.find(node => (node.type() as typeof AdornisEntity)._class === e._class);
      const entity = node?.type() as Constructor<any>;
      return new entity(e);
    });

    let productResult: { requirementsMet: true } | { requirementsMet: false; reason: any } = { requirementsMet: true };
    for (const entryRequirement of entryRequirements) {
      const requirementIsMet = await entryRequirement.requirementIsMet(userID);
      if (!requirementIsMet) {
        productResult = { requirementsMet: false, reason: await entryRequirement.label() };
        break;
      }
    }
    result.set(productID, productResult);
  }

  return result;
}

export const subscribeProductEntryRequirementsForProduct = registerSubscription({
  operationName: 'subscribeProductEntryRequirementsForProduct',
  resolve:
    (productID: string) =>
    <SelSet extends BaseQLSelectionSet<ProductEntryRequirement>>(gqlFields: SelSet) => {
      return watchEntireCollection(ProductEntryRequirement._collectionName).pipe(
        switchMap(async () => {
          const collection = await scopedCollection(ProductEntryRequirement._class);
          const result = await collection.find<ProductEntryRequirement>({ productID }).toArray();
          return result as unknown as ProductEntryRequirement[];
        }),
      );
    },
  type: () => [ProductEntryRequirement],
  params: [{ name: 'productID', type: () => ID }],
});

export const checkWeatherUserMeetsEntryRequirementsQuery = registerQuery({
  operationName: 'checkWeatherUserMeetsEntryRequirements',
  resolve: (userID: string, productID: string) => async () => {
    if (!productID) return true;
    const product = await getProductByID(productID)({
      settings: {
        allowSubscribingToWaitinglistWithoutMeetingRequirements: 1,
        inheritedAllowSubscribingToWaitinglistWithoutMeetingRequirements: 1,
      },
    });
    if (!product) throw new Error('product not found: ' + productID);

    if (product.settings?.allowSubscribingToWaitinglistWithoutMeetingRequirements) return true;
    if (product.settings?.inheritedAllowSubscribingToWaitinglistWithoutMeetingRequirements?.[1] === 'true') return true;

    const result = await checkWeatherUserMeetsEntryRequirements(userID, productID);
    if (!result.requirementsMet) {
      const errMesg = `Das Produkt kann nicht gebucht werden, da die Anforderung '${result.reason[0]}' nicht erfüllt wird.`;
      throw new Error(errMesg);
    }
    return true;
  },
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'productID', type: () => String },
  ],
});

export const getProductEntryRequirementLabels = registerQuery({
  operationName: 'getProductEntryRequirementLabels',
  params: [{ name: 'productID', type: () => String }],
  type: () => [String],
  resolve: function (productID: string) {
    return async () => {
      if (!productID) return [];

      const collection = await scopedCollection(ProductEntryRequirement._class);
      const result = await collection.find<ProductEntryRequirement>({ productID }).toArray();
      const requirements = result.map(data => mongoDataToEntity(data));
      const labels = Promise.all(requirements.map(r => r.label()));
      return labels;
    };
  },
});
